import React, { FC } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { TToastNewState } from 'transfix-ui/components/Toast'
import { Box } from 'transfix-ui/core'
import * as yup from 'yup'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { ResponsiveLayoutRow } from 'apps/fleet-planner/components/layout/ResponsiveLayoutRow'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { getUserNamesFromEntity } from 'apps/fleet-planner/utils/getUserData'

interface INameForm {
  /**
   * First name of the user
   */
  firstName: string

  /**
   * Last name of the user
   */
  lastName: string
}

const EMPTY_VALIDATION_MESSAGE = 'This field cannot be empty.'

const NameFormSchema = yup.object<INameForm>({
  firstName: yup.string().required(EMPTY_VALIDATION_MESSAGE),
  lastName: yup.string().required(EMPTY_VALIDATION_MESSAGE),
})

type TNameFormSchema = yup.InferType<typeof NameFormSchema>

interface INameMobilePageProps {
  /**
   * A function provided by the parent component to navigate to the default page
   */
  onGoToDefaultRoute: () => void

  /**
   * A function provided by the parent component to open the toast on the main page
   */
  onOpenToast?: (options: TToastNewState) => void
}

export const NameMobilePage: FC<INameMobilePageProps> = ({
  onGoToDefaultRoute,
  onOpenToast = () => {},
}) => {
  const { trackEvent } = useFMSAnalytics()
  const { errors, updateUserInfo, userData } = useUserContext()
  const { firstName: initialFirstName, lastName: initialLastName } = getUserNamesFromEntity(
    userData
  )
  const { control, errors: formErrors, formState, handleSubmit } = useForm({
    resolver: yupResolver(NameFormSchema),
    defaultValues: NameFormSchema.cast({
      firstName: initialFirstName,
      lastName: initialLastName,
    }),
  })

  const pressBackButtonHandler = () => {
    onGoToDefaultRoute()
  }

  const submitFormHandler = async (data: TNameFormSchema) => {
    const { firstName, lastName } = data || {}
    const payload = {
      user: {
        firstName,
        lastName,
      },
    }
    try {
      await updateUserInfo(payload)
      trackEvent('Settings', 'SAVED_GENERAL_SETTINGS', payload)
      onGoToDefaultRoute()
      onOpenToast({
        toastType: errors ? 'alert' : 'default',
        toastMessage: errors || 'Changes Saved.',
      })
    } catch (error) {
      onOpenToast({
        toastType: 'alert',
        toastMessage: 'Something went wrong.',
      })
    }
  }

  const isDisabled = !formState.isDirty || formState.isSubmitting
  const submitHandler = handleSubmit(submitFormHandler)

  return (
    <ResponsiveLayout
      title='Name'
      onPressBackButton={pressBackButtonHandler}
      footer={
        <Button
          data-test='submit-name'
          type='submit'
          color='primary'
          size='large'
          disabled={isDisabled}
          onClick={submitHandler}
          fullWidth>
          Save changes
        </Button>
      }>
      <ResponsiveLayoutRow>
        <form onSubmit={submitHandler}>
          <ControlledTextField
            control={control}
            name='firstName'
            TextInputProps={{
              dataTest: 'first-name',
              label: 'First Name',
              placeholder: 'First Name',
              error: !!formErrors?.firstName,
              validationMessage: formErrors?.firstName?.message,
              large: true,
            }}
          />
          <Box pt={4}>
            <ControlledTextField
              control={control}
              name='lastName'
              TextInputProps={{
                dataTest: 'last-name',
                label: 'Last Name',
                placeholder: 'Last Name',
                error: !!formErrors?.lastName,
                validationMessage: formErrors?.lastName?.message,
                large: true,
              }}
            />
          </Box>
        </form>
      </ResponsiveLayoutRow>
    </ResponsiveLayout>
  )
}
